import React, { Children, memo, useMemo } from 'react'
import { Maybe } from 'types/graphqlTypes'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { ICarrierPageFragment } from '../Carrier/graphql/CarrierPageFragment'
import { Notes } from '../Notes'
import CarrierProfileSidebar from '../Sidebar/CarrierProfileSidebar'
import CarrierDiverseCertificationsCard from '../Sidebar/components/CarrierDiverseCertificationsCard'
import FleetInformationCard from '../Sidebar/components/FleetInformationCard/FleetInformationCard'
import LoadPreferencesCard from '../Sidebar/components/LoadPreferencesCard'
import SmartWayCarrierCard from '../Sidebar/components/SmartWayCarrierCard'
import { Capacity } from './Capacity'
import { CarrierSummary } from './CarrierSummary'
import FuelSurcharge from './FuelSurcharge'
import { Instructions } from './Instructions'
import { LaneAlerts } from './LaneAlerts'
import { LaneHistory } from './LaneHistory'
import { Managers } from './Managers'
import { RoutingGuides } from './RoutingGuides'
import { SavedLanes } from './SavedLanes'
import { TrackingLocation } from './TrackingLocation/TrackingLocation'

const Column = memo<{ className?: string; children?: {} }>(({ children, className }) => (
  <div className={className}>
    {Children.map(children, section => {
      if (section) return <div className='pv4'>{section}</div>
      return null
    })}
  </div>
))

export interface IGeneralInformationProps {
  carrierApplication?: Maybe<ICarrierPageFragment>
}

export const GeneralInformation = memo<IGeneralInformationProps>(({ carrierApplication }) => {
  const { carrierProfile, id: carrierApplicationId } = carrierApplication || {}
  const {
    certifications,
    dedicatedLaneRatePreference,
    endorsements,
    equipmentCapability,
    excludedCommodities,
    freightCapability,
    loadingCapability,
    rateTypePreference,
    smartWayRegistration,
  } = carrierProfile ?? {}

  const { getFlag } = useFeatureFlagContext()
  const { enabled: showRoutingGuides } = getFlag('newRoutingGuides')
  const { enabled: isSavedLanesEnabled } = getFlag('enableSavedLanesMP')
  const { enabled: isNewLaneAlertsEnabled } = getFlag('newLaneAlerts')
  const { enabled: isNewCarrierProfileSidebarEnabled } = getFlag('newCarrierProfileSidebar')

  const showSavedLanes = useMemo(() => carrierApplicationId !== undefined && isSavedLanesEnabled, [
    carrierApplicationId,
    isSavedLanesEnabled,
  ])
  const showLaneAlerts = useMemo(() => !showSavedLanes && isNewLaneAlertsEnabled, [
    showSavedLanes,
    isNewLaneAlertsEnabled,
  ])

  return (
    <div className='row' data-test='carrier-overview-tab-content'>
      <Column className='col-md-4'>
        {isNewCarrierProfileSidebarEnabled && <CarrierProfileSidebar />}
        <CarrierSummary />
        {isNewCarrierProfileSidebarEnabled && carrierApplicationId && (
          <FleetInformationCard
            carrierApplicationId={carrierApplicationId}
            fleetInfo={{
              equipmentCapability,
              freightCapability,
              endorsements,
            }}
          />
        )}
        <TrackingLocation />
        {isNewCarrierProfileSidebarEnabled && carrierApplicationId && (
          <LoadPreferencesCard
            carrierApplicationId={carrierApplicationId}
            loadPreferences={{
              dedicatedLaneRatePreference,
              excludedCommodities,
              loadingCapability,
              rateTypePreference,
            }}
          />
        )}
        {isNewCarrierProfileSidebarEnabled && carrierApplicationId && (
          <CarrierDiverseCertificationsCard
            carrierApplicationId={carrierApplicationId}
            certifications={certifications ?? []}
          />
        )}
        {isNewCarrierProfileSidebarEnabled && carrierApplicationId && (
          <SmartWayCarrierCard
            carrierApplicationId={carrierApplicationId}
            smartWayRegistration={smartWayRegistration}
          />
        )}
        <Managers />
        <Instructions />
        <Notes />
      </Column>
      <Column className='col-md-8'>
        <LaneHistory />
        <Capacity />
        {showRoutingGuides && <RoutingGuides />}
        {showLaneAlerts && <LaneAlerts />}
        {showSavedLanes && carrierApplicationId && (
          <SavedLanes carrierApplicationId={carrierApplicationId} />
        )}
        <FuelSurcharge />
      </Column>
    </div>
  )
})
